import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  HoverableLinkDesigns,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { CspSessionType } from 'src/app/CspSignin/redux/cspSignin.reducer'
import BrandMarkLogo from 'src/components/BrandMarkLogo/BrandMarkLogo'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { useSessionStatus } from 'src/context/SessionStatusProvider/useSessionStatus'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { AvatarSizes } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import Lnk from 'src/navigation/Lnk'
import { cspUserDetailUrl, faqUrl } from 'src/navigation/urls'
import {
  selectCspUserListModelFromState,
  selectStartTimeStampFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectExternalFAQUrl } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

interface CspTopBarProps {
  selectedCspSessionType?: CspSessionType
  resetCspSigninState?: () => void
  deleteSignout: () => void
  deleteCspEmulatedSessions?: (shouldDeleteSignout: boolean) => void
}

const CspTopBar: React.FC<CspTopBarProps> = (props): React.ReactElement => {
  const translate = useTranslate()

  const user = useCurrentUser()
  const cspUser = useSelector(selectCspUserListModelFromState)
  const { isAuthenticated, isCspAuthenticated } = useSessionStatus()
  const externalFAQUrl = useSelector(selectExternalFAQUrl)
  const startTimeStamp = useSelector(selectStartTimeStampFromState)

  // csp timer
  const cspTimerInterval = useRef<NodeJS.Timeout | undefined>()
  const [timer, setTimer] = useState<string>('')
  useEffect(() => {
    if (isCspAuthenticated && startTimeStamp) {
      const timerStartTimestamp = moment(startTimeStamp, 'HH:mm:ss')
      const now = moment(new Date(), 'HH:mm:ss')
      cspTimerInterval.current = setInterval(() => {
        now.add(1, 'second')
        setTimer(moment.utc(now.diff(timerStartTimestamp)).format('HH:mm:ss'))
      }, 1000)
    }
    return (): void => {
      if (cspTimerInterval.current !== undefined) {
        clearInterval(cspTimerInterval.current)
      }
    }
  }, [isCspAuthenticated, startTimeStamp])

  return (
    <div
      css={css`
        width: 100%;
      `}
    >
      {isAuthenticated && (
        <div
          css={css`
            height: 64px;
          `}
        />
      )}
      <div
        css={css`
          align-items: center;
          justify-content: space-between;
          width: 100%;
          display: flex;
          background-color: ${themeConstants.colors.font_primary};
          height: 64px;

          ${isAuthenticated &&
          `
            position: fixed;
            top: 0;
            z-index: 1;
          `}
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            padding: 20px 20px;

            ${getBreakpointDown(breakpointSizes.XSM)(css`
              display: none;
            `)}
          `}
        >
          {(user === undefined || !isAuthenticated) && (
            <>
              <BrandMarkLogo knockout />
              <span
                css={css`
                  margin-left: 10px;
                  ${getFontCss(
                    16,
                    styleConstants.colors.white,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                {translate('Customer Support')}
              </span>
            </>
          )}
          {user !== undefined && isAuthenticated && (
            <>
              <Lnk
                to={cspUserDetailUrl(user?.id)}
                css={css`
                  padding: 6px 12px;
                  background-color: ${styleConstants.colors.white};
                  border-radius: 16px;
                  display: flex;
                  align-items: center;
                  margin-right: 10px;
                  ${getFontCss(
                    12,
                    themeConstants.colors.cta,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
                ariaLabelSource={'Customer details'}
              >
                {user !== undefined && (
                  <UserAvatar
                    css={css`
                      margin-right: 4px;
                    `}
                    user={user}
                    size={AvatarSizes.Small}
                  />
                )}
                {translate('Customer details')}
              </Lnk>
              <HoverableLink
                underline
                design={HoverableLinkDesigns.LightOnDark}
                href={externalFAQUrl || faqUrl()}
                ariaLabelSource={'Support FAQs'}
              >
                {translate('Support FAQs')}
              </HoverableLink>
            </>
          )}
        </div>
        <div
          css={css`
            display: flex;
            align-self: flex-start;
          `}
        >
          {props.selectedCspSessionType !== undefined && (
            <>
              <div
                css={css`
                  padding: 16px;
                  background-color: ${styleConstants.colors.red};
                  border-bottom-left-radius: 10px;
                  display: flex;
                  flex-wrap: wrap;
                  align-content: center;
                `}
              >
                <span
                  css={css`
                    display: flex;
                    flex-direction: row;
                    ${getFontCss(
                      12,
                      styleConstants.colors.white,
                      styleConstants.fontFamilies.regular,
                    )};
                  `}
                >
                  {translate('In a session')}
                  <span
                    css={css`
                      padding-left: 5px;
                    `}
                  >
                    {timer}
                  </span>
                </span>
              </div>
              <div
                css={css`
                  padding: 16px;
                  background-color: ${styleConstants.colors.red_dark};
                  border-bottom-right-radius: 10px;
                  display: flex;
                  flex-wrap: wrap;
                  align-content: center;
                `}
              >
                <HoverableLink
                  design={HoverableLinkDesigns.LightOnDark}
                  underline
                  onClick={(): void => props.resetCspSigninState?.()}
                  size={HoverableLinkSizes.Small}
                  ariaLabelSource={'End Session'}
                >
                  {translate('End Session')}
                </HoverableLink>
              </div>
            </>
          )}
          {isAuthenticated && (
            <>
              <div
                css={css`
                  padding: 16px;
                  background-color: ${styleConstants.colors.yellow_light};
                  border-bottom-left-radius: 10px;
                  display: flex;
                  flex-wrap: wrap;
                  align-content: center;
                `}
              >
                <span
                  css={css`
                    display: flex;
                    flex-direction: row;
                    ${getFontCss(
                      12,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.regular,
                    )};
                  `}
                >
                  {translate('In a session')}
                  <span
                    css={css`
                      padding-left: 5px;
                    `}
                  >
                    {timer}
                  </span>
                </span>
              </div>
              <div
                css={css`
                  padding: 16px;
                  background-color: ${styleConstants.colors.yellow_dark};
                  border-bottom-right-radius: 10px;
                  display: flex;
                  flex-wrap: wrap;
                  align-content: center;
                `}
              >
                <HoverableLink
                  design={HoverableLinkDesigns.Primary}
                  underline
                  onClick={(): void => {
                    props.deleteCspEmulatedSessions?.(false)
                  }}
                  size={HoverableLinkSizes.Small}
                  ariaLabelSource={'End Session'}
                >
                  {translate('End Session')}
                </HoverableLink>
              </div>
            </>
          )}
        </div>
        <div
          css={css`
            padding: 20px 20px;
          `}
        >
          {cspUser !== undefined && (
            <>
              <span
                css={css`
                  margin-right: 10px;
                  ${getFontCss(
                    12,
                    styleConstants.colors.white,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                {translate('Logged in as ${user}', {
                  user: (
                    <span
                      css={css`
                        ${getFontCss(
                          12,
                          styleConstants.colors.white,
                          styleConstants.fontFamilies.medium,
                        )};
                      `}
                    >
                      {cspUser.firstNameOrEmail}
                    </span>
                  ),
                })}
              </span>
              <HoverableLink
                design={HoverableLinkDesigns.LightOnDark}
                underline
                onClick={(): void => {
                  isAuthenticated
                    ? props.deleteCspEmulatedSessions?.(true)
                    : props.deleteSignout()
                }}
                ariaLabelSource={'Log out'}
              >
                {translate('Log out')}
              </HoverableLink>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default CspTopBar
